import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';

const DAY_MAP = {
  'Domingo': 0, 'Lunes': 1, 'Martes': 2, 'Miércoles': 3,
  'Jueves': 4, 'Viernes': 5, 'Sábado': 6,
};

// Hardcoded weekly events — instant load, no Firestore needed
const WEEKLY_EVENTS = [
  { id: 'w1', title: 'Servicio Dominical', day: 'Domingo', time: '10:30 AM', location: 'Salón Principal' },
  { id: 'w2', title: 'Clase de Oración', day: 'Domingo', time: '9:15 AM', location: 'Salón Principal' },
  { id: 'w3', title: 'Estudio Bíblico', day: 'Miércoles', time: '6:30 PM', location: 'Salón A236' },
  { id: 'w4', title: 'Grupo de Crecimiento', day: 'Viernes', time: '6:30 PM', location: 'Casas' },
];

function parseTime(timeStr) {
  if (!timeStr) return 0;
  const [time, meridiem] = timeStr.split(' ');
  let [h, m] = time.split(':').map(Number);
  if (meridiem === 'PM' && h !== 12) h += 12;
  if (meridiem === 'AM' && h === 12) h = 0;
  return h * 60 + m;
}

function minutesUntil(dayName, timeStr) {
  const now = new Date();
  const currentDay = now.getDay();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const serviceDay = DAY_MAP[dayName] ?? 0;
  const serviceMinutes = parseTime(timeStr);
  let diff = (serviceDay - currentDay) * 24 * 60 + (serviceMinutes - currentMinutes);
  if (diff <= 0) diff += 7 * 24 * 60;
  return diff;
}

const sortedWeekly = [...WEEKLY_EVENTS].sort((a, b) => minutesUntil(a.day, a.time) - minutesUntil(b.day, b.time));

let specialCache = null;

export default function Events({ navigate }) {
  const [special, setSpecial] = useState(specialCache || []);
  const [loading, setLoading] = useState(!specialCache);

  useEffect(() => {
    if (specialCache) return;
    const fetchSpecial = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'events'));
        const now = new Date();
        const s = snapshot.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(e => e.active && e.type === 'special' && e.date?.toDate() >= now)
          .sort((a, b) => a.date.toDate() - b.date.toDate());
        specialCache = s;
        setSpecial(s);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchSpecial();
  }, []);

  const formatDate = (timestamp) => {
    if (!timestamp) return { month: '---', day: '--' };
    const d = timestamp.toDate();
    return {
      month: d.toLocaleString('es-MX', { month: 'short' }).toUpperCase(),
      day: d.getDate(),
      time: d.toLocaleString('es-MX', { hour: 'numeric', minute: '2-digit', hour12: true }),
    };
  };

  return (
    <>
      <h2 className="page-title">📅 Próximos Eventos</h2>

      {/* Weekly Services — instant, no loading */}
      <div className="card">
        <h3 style={{ marginBottom: 12 }}>🔁 Servicios Semanales</h3>
        {sortedWeekly.map((e, i) => (
          <div className="event-item" key={e.id} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <div className="event-date" style={{ background: i === 0 ? '#c0392b' : '#2d4a8a' }}>
              <div className="month" style={{ fontSize: '0.6rem' }}>{e.day?.substring(0, 3).toUpperCase()}</div>
              <div style={{ fontSize: '0.75rem', fontWeight: 700 }}>🔁</div>
            </div>
            <div className="event-info" style={{ flex: 1 }}>
              <h4 style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                {e.title}
                {i === 0 && (
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: '#ef4444', display: 'inline-block',
                    animation: 'pulse-dot 1.2s ease-in-out infinite',
                    flexShrink: 0,
                  }} />
                )}
              </h4>
              <p>{e.time}{e.location ? ` — ${e.location}` : ''}</p>
              {i === 0 && <p style={{ fontSize: '0.75rem', color: '#ef4444', fontWeight: 600, marginTop: 2 }}>Próximo servicio</p>}
            </div>
            {e.title?.toLowerCase().includes('crecimiento') && (
              <button
                onClick={() => navigate('join-group')}
                style={{
                  background: 'var(--gold)', color: 'var(--navy)', border: 'none',
                  borderRadius: 8, padding: '6px 14px', fontSize: '0.78rem',
                  fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                  whiteSpace: 'nowrap', flexShrink: 0,
                }}
              >
                Únete
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Special Events */}
      <div className="card">
        <h3 style={{ marginBottom: 12 }}>⭐ Eventos Especiales</h3>
        {loading && <p style={{ textAlign: 'center', color: '#888', padding: '20px 0' }}>Cargando...</p>}
        {!loading && special.length === 0 && (
          <p style={{ color: '#888', fontSize: '0.9rem' }}>Te estaremos informando del próximo evento.</p>
        )}
        {!loading && special.map((e) => {
          const { month, day, time } = formatDate(e.date);
          return (
            <div className="event-item" key={e.id}>
              <div className="event-date">
                <div className="month">{month}</div>
                <div className="day">{day}</div>
              </div>
              <div className="event-info">
                <h4>{e.title}</h4>
                <p>{time}{e.location ? ` — ${e.location}` : ''}</p>
                {e.description && <p style={{ marginTop: 2 }}>{e.description}</p>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Address */}
      <div className="card">
        <h3>📍 Dirección</h3>
        <p>4700 N Beach St<br />Fort Worth, TX</p>
        <a href="https://maps.apple.com/?q=4700+N+Beach+St+Fort+Worth+TX" target="_blank" rel="noreferrer" className="btn" style={{ marginTop: 14, display: 'inline-flex' }}>
          🗺 Ver en Mapas
        </a>
      </div>

      {/* Contact */}
      <div className="card">
        <h3>📞 Contáctanos</h3>
        <p>Teléfono: <a href="tel:8179957398">(817) 995-7398</a></p>
        <p style={{ marginTop: 8 }}>Sitio Web: <a href="https://bethesdacommunitychurch.com/home-2/" target="_blank" rel="noreferrer">bethesdacommunitychurch.com</a></p>
      </div>
    </>
  );
}
